import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { FilePdf, Trash, Receipt, ArrowsCounterClockwise } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { downloadBlob } from '../utils/download';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreditNotes = () => {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCreditNotes();
  }, []);

  const fetchCreditNotes = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_URL}/api/credit-notes`, { withCredentials: true });
      setCreditNotes(r.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async (cn) => {
    try {
      const r = await axios.get(`${API_URL}/api/credit-notes/${cn.id}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      downloadBlob(r.data, `avoir_${cn.credit_note_number}.pdf`, 'application/pdf');
      toast.success('Avoir téléchargé');
    } catch (e) {
      toast.error('Erreur téléchargement');
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm('Supprimer cet avoir ? Le stock sera ré-décrémenté et le mouvement de caisse annulé.'))) return;
    try {
      await axios.delete(`${API_URL}/api/credit-notes/${id}`, { withCredentials: true });
      toast.success('Avoir supprimé');
      fetchCreditNotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const filtered = creditNotes.filter(c =>
    !searchQuery ||
    c.credit_note_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.invoice_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.client_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="credit-notes-page">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Avoirs</h1>
            <p className="text-slate-600">Liste des avoirs émis sur les factures</p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 flex items-center gap-2">
          <ArrowsCounterClockwise size={20} className="text-blue-600" />
          <p className="text-sm text-blue-800">
            Pour émettre un nouvel avoir, allez dans la page <strong>Factures</strong> et cliquez sur l'icône "Avoir" à côté d'une facture.
          </p>
        </div>

        <div className="mb-4">
          <Input
            placeholder="Rechercher par n° avoir, facture ou client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-credit-notes-input"
            className="w-full md:w-96"
          />
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">N° Avoir</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Facture liée</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Client</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Montant</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Motif</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Date</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Émis par</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((cn) => (
                  <tr key={cn.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm text-slate-900 font-medium">
                      <div className="flex items-center gap-2">
                        <Receipt size={16} className="text-red-600" />
                        {cn.credit_note_number}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-blue-600">{cn.invoice_number}</td>
                    <td className="py-3 px-4 text-sm text-slate-900">{cn.client_name}</td>
                    <td className="py-3 px-4 text-sm text-red-600 text-right font-bold">-{cn.total_amount.toFixed(2)} €</td>
                    <td className="py-3 px-4 text-sm text-slate-600 max-w-xs truncate">{cn.reason || '-'}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{new Date(cn.created_at).toLocaleDateString('fr-FR')}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{cn.issued_by}</td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => downloadPdf(cn)}
                        data-testid={`download-credit-note-${cn.id}`}
                        className="inline-flex p-2 text-red-600 hover:bg-red-50 rounded-md mr-1"
                        title="Télécharger PDF"
                      >
                        <FilePdf size={18} />
                      </button>
                      {user?.role === 'admin' && (
                        <button
                          onClick={() => handleDelete(cn.id)}
                          data-testid={`delete-credit-note-${cn.id}`}
                          className="inline-flex p-2 text-red-700 hover:bg-red-50 rounded-md"
                          title="Supprimer"
                        >
                          <Trash size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="8" className="py-12 text-center text-slate-500">Aucun avoir</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditNotes;
